import { SearchOutlined } from "@ant-design/icons";
import { Button, Input } from "antd";
import React, { useMemo, useState } from "react";
import HBButton from "../HBButton";
import HBEmpty from "../HBEmpty";
import HBIcon from "../HBIcon";
import HBLoading from "../HBLoading";
import HBModal, { type HBModalProps } from "../HBModal";
import styles from "./index.module.less";

export interface SelectItem {
  key: string;
  label: string;
  count?: number;
  disabled?: boolean;
  pinyin?: string;
}

export interface DialogSelectProps {
  loading?: boolean;
  placeholder?: string;
  items: SelectItem[];
  selectedKeys?: string[];
  multiple?: boolean;
  showCount?: boolean;
  showManageButton?: boolean;
  manageButtonText?: string;
  confirmButtonText?: string;
  searchable?: boolean;
  onManage?: () => void;
  onSearch?: (keyword: string) => void;
  onOk?: (selectedKeys: string[]) => void;
}

const DialogSelect: React.FC<DialogSelectProps & HBModalProps> = ({
  loading = false,
  placeholder = "搜索...",
  items = [],
  selectedKeys = [],
  multiple,
  showCount = true,
  showManageButton = true,
  manageButtonText = "专辑管理",
  confirmButtonText = "确认",
  searchable = true,
  onManage,
  onSearch,
  ...props
}) => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [internalSelectedKeys, setInternalSelectedKeys] =
    useState<string[]>(selectedKeys);

  // 搜索过滤
  const filteredItems = useMemo(() => {
    if (!searchKeyword.trim()) return items;
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        item.key.toLowerCase().includes(searchKeyword.toLowerCase())
    );
  }, [items, searchKeyword]);

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    onSearch?.(value);
  };

  // 处理选择
  const handleSelect = (key: string, checked: boolean) => {
    let newSelectedKeys: string[];

    if (multiple) {
      if (checked) {
        newSelectedKeys = [...internalSelectedKeys, key];
      } else {
        newSelectedKeys = internalSelectedKeys.filter((k) => k !== key);
      }
    } else {
      newSelectedKeys = checked ? [key] : [];
    }

    setInternalSelectedKeys(newSelectedKeys);
  };

  // 处理确认
  const handleConfirm = () => {
    props.onOk?.(internalSelectedKeys);
  };

  // 处理关闭
  const handleClose = () => {
    setSearchKeyword("");
    setInternalSelectedKeys(selectedKeys);
    props.onCancel?.();
  };

  return (
    <HBModal
      {...props}
      onCancel={handleClose}
      footer={null}
      centered
      className={styles.dialogSelect}
      width={400}
    >
      <div className={styles.container}>
        {/* 搜索框 */}
        {searchable && (
          <div className={styles.searchBox}>
            <Input
              placeholder={placeholder}
              value={searchKeyword}
              onChange={(e) => handleSearch(e.target.value)}
              prefix={<SearchOutlined className={styles.searchIcon} />}
              className={styles.searchInput}
            />
          </div>
        )}

        {/* 选择列表 */}
        <div className={styles.listContainer}>
          {filteredItems.map((item) => {
            const isSelected = internalSelectedKeys.includes(item.key);
            return (
              <div
                key={item.key}
                className={`${styles.listItem}`}
                onClick={() =>
                  !item.disabled && handleSelect(item.key, !isSelected)
                }
              >
                {item.pinyin ? (
                  <div className={styles.itemPinyin}>{item.pinyin}</div>
                ) : (
                  <div className={styles.itemPinyin}></div>
                )}
                <div
                  className={`${styles.itemContent} ${
                    isSelected ? styles.selected : ""
                  } ${item.disabled ? styles.disabled : ""}`}
                >
                  <span className={styles.itemLabel}>{item.label}</span>
                  {isSelected ? (
                    <HBIcon type="ic_check" />
                  ) : (
                    showCount &&
                    typeof item.count !== "undefined" && (
                      <span className={styles.itemCount}>{item.count}</span>
                    )
                  )}
                </div>
              </div>
            );
          })}

          {filteredItems.length === 0 && !loading && (
            <HBEmpty
              imageStyle={{ height: 50, marginTop: 72 }}
              subtitle="暂无数据"
            />
          )}

          {loading && (
            <div className={styles.loading}>
              <HBLoading />
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div className={styles.footer}>
          {showManageButton && (
            <Button onClick={onManage} className={styles.manageBtn}>
              {manageButtonText}
            </Button>
          )}
          <HBButton type="primary" onClick={handleConfirm}>
            {confirmButtonText}
          </HBButton>
        </div>
      </div>
    </HBModal>
  );
};

export default DialogSelect;
